import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  rolePermissionsService,
  type CreateRolePermissionData,
} from "../../services/admin/rolepermissions.service";

export function useRolePermissions() {
  return useQuery({
    queryKey: ["admin", "role_permissions"],
    queryFn: rolePermissionsService.getAll,
  });
}

export function useRolePermissionsByRoleId(roleId: string) {
  return useQuery({
    queryKey: ["admin", "role_permissions", "by_role", roleId],
    queryFn: () => rolePermissionsService.getByRoleId(roleId),
    enabled: !!roleId,
  });
}

export function useCreateRolePermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRolePermissionData) =>
      rolePermissionsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "role_permissions"],
      });
      notifications.show({
        title: "Success",
        message: "Permission added to role successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to add permission to role",
        color: "red",
      });
    },
  });
}

export function useDeleteRolePermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => rolePermissionsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "role_permissions"],
      });
      notifications.show({
        title: "Success",
        message: "Permission removed from role successfully",
        color: "green",
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to remove permission from role",
        color: "red",
      });
    },
  });
}
